import { FormEvent, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { intakeQuestions } from '../data/questions';
import { catalogue, deriveSafetyNotes, evaluateFormulas } from '../data/formulas';
import {
  ConversationMessage,
  FormulaMatch,
  IntakeAnswers,
  IntakeQuestion,
  QuestionOption
} from '../types/conversation';
import '../styles/chat.css';

const API_BASE = '%%API_BASE%%';

const createMessage = (
  role: ConversationMessage['role'],
  text: string,
  variant: ConversationMessage['variant'] = 'neutral'
): ConversationMessage => ({
  id: `${role}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  role,
  text,
  timestamp: Date.now(),
  variant
});

const findOptionLabel = (value: string, options: QuestionOption[]): string => {
  const match = options.find((option) => option.value === value);
  return match ? match.label : value;
};

const normaliseValue = (
  question: IntakeQuestion,
  rawValue: string | string[]
): string | string[] | undefined => {
  if (question.id === 'safetyFlags') {
    const arrayValue = Array.isArray(rawValue) ? rawValue : [rawValue];
    const filtered = arrayValue.filter((entry) => entry !== 'none');
    return filtered.length ? filtered : undefined;
  }

  if (question.id === 'tongue' && rawValue === 'skip') {
    return undefined;
  }

  if (question.id === 'cycle' && rawValue === 'not-applicable') {
    return undefined;
  }

  return rawValue;
};

const ChatExperience = (): JSX.Element => {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [answers, setAnswers] = useState<IntakeAnswers>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [pendingText, setPendingText] = useState('');
  const [pendingChoice, setPendingChoice] = useState('');
  const [pendingMulti, setPendingMulti] = useState<string[]>([]);
  const [recommendations, setRecommendations] = useState<FormulaMatch[]>([]);
  const [safetyNotes, setSafetyNotes] = useState<string[]>([]);
  const [completed, setCompleted] = useState(false);

  const currentQuestion = intakeQuestions[currentQuestionIndex];

  const addMessage = (message: ConversationMessage) => {
    setMessages((prev) => [...prev, message]);
  };

  const introMessages = useMemo<ConversationMessage[]>(
    () => [
      createMessage(
        "assistant",
        "Hi, I'm Soupcon -- your AI guide trained alongside licensed TCM practitioners. I can't diagnose or prescribe, but I translate your story into patterns our clinicians review before anything ships.",
        "info"
      ),
      createMessage(
        "assistant",
        "Let's build a quick picture. I'll ask a handful of questions that help us match existing herbal formulas and, when needed, queue a human consult.",
        "positive"
      ),
      createMessage("assistant", intakeQuestions[0].prompt, "info")
    ],
    []
  );

  useEffect(() => {
    setMessages((prev) => (prev.length ? prev : introMessages));
  }, [introMessages]);

  useEffect(() => {
    setPendingText('');
    setPendingChoice('');
    setPendingMulti([]);
  }, [currentQuestionIndex]);

  const conversationHeightClass = useMemo(() => {
    return completed ? 'chat-stream tall' : 'chat-stream';
  }, [completed]);

  const handleMultiToggle = (value: string) => {
    setPendingMulti((prev) => {
      const alreadySelected = prev.includes(value);
      let nextSelection = alreadySelected
        ? prev.filter((item) => item !== value)
        : [...prev, value];

      if (value === 'none') {
        nextSelection = alreadySelected ? [] : ['none'];
      } else {
        nextSelection = nextSelection.filter((item) => item !== 'none');
      }

      return nextSelection;
    });
  };

  const formatAnswer = (
    question: IntakeQuestion,
    rawValue: string | string[]
  ): string => {
    if (question.type === 'text') {
      return String(rawValue);
    }

    const arrayValue = Array.isArray(rawValue) ? rawValue : [rawValue];
    const formatted = arrayValue.map((entry) => findOptionLabel(entry, question.options));
    return formatted.join(', ');
  };

  const pushNextQuestion = (index: number) => {
    const question = intakeQuestions[index];
    addMessage(
      createMessage(
        'assistant',
        question.prompt,
        'info'
      )
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    const question = currentQuestion;
    if (!question) {
      return;
    }

    let rawValue: string | string[] | null = null;
    if (question.type === 'text') {
      if (!pendingText.trim()) {
        return;
      }
      rawValue = pendingText.trim();
    } else if (question.type === 'choice') {
      if (!pendingChoice) {
        return;
      }
      rawValue = pendingChoice;
    } else if (question.type === 'multi') {
      if (!pendingMulti.length) {
        return;
      }
      rawValue = pendingMulti;
    }

    if (rawValue === null) {
      return;
    }

    const displayAnswer = formatAnswer(question, rawValue);
    const normalised = normaliseValue(question, rawValue);
    const nextAnswers: IntakeAnswers = { ...answers };

    if (normalised === undefined) {
      delete nextAnswers[question.id];
    } else {
      nextAnswers[question.id] = normalised;
    }

    setAnswers(nextAnswers);
    addMessage(createMessage('user', displayAnswer));

    const nextIndex = currentQuestionIndex + 1;

    if (nextIndex < intakeQuestions.length) {
      setCurrentQuestionIndex(nextIndex);
      pushNextQuestion(nextIndex);
      return;
    }

    const matches = evaluateFormulas(nextAnswers);
    const safety = deriveSafetyNotes(nextAnswers);
    setRecommendations(matches);
    setSafetyNotes(safety);
    setCompleted(true);

    addMessage(
      createMessage(
        "assistant",
        "Thanks for sharing. I'll package this for a clinician to double-check and tee up formulas that respect your goals and safety notes.",
        "positive"
      )
    );

    addMessage(
      createMessage(
        "assistant",
        `Nothing here is a medical prescription. We'll document these insights and sync with a licensed practitioner via ${API_BASE}/intake before anything ships.`,
        "alert"
      )
    );
  };

  const resetIntake = () => {
    setAnswers({});
    setRecommendations([]);
    setSafetyNotes([]);
    setCompleted(false);
    setCurrentQuestionIndex(0);
    setMessages(introMessages);
  };

  const recommendationsHeader = completed
    ? "Preliminary matches from today's conversation"
    : "We'll highlight formulas here once we've learned a bit more about you.";

  return (
    <section className="section-shell" id="experience">
      <div className="experience-grid glass-panel">
        <div className="chat-column">
          <header className="chat-header">
            <span className="tag-pill">Guided intake - ~4 minutes</span>
            <h2>Tell us your story, get a grounded TCM starting point</h2>
            <p>
              Answer a few reflective prompts. Soupcon maps them to traditional patterns,
              flags safety notes for our practitioners, and drafts a formula recommendation
              plan that you can discuss before subscribing.
            </p>
          </header>

          <div className={conversationHeightClass}>
            {messages.map((message) => (
              <div key={message.id} className={clsx('chat-bubble', `role-${message.role}`, `variant-${message.variant ?? 'neutral'}`)}>
                <p>{message.text}</p>
              </div>
            ))}
          </div>

          {!completed && currentQuestion && (
            <form className="chat-input" onSubmit={handleSubmit}>
              {currentQuestion.helper && <p className="helper-text">{currentQuestion.helper}</p>}

              {currentQuestion.type === 'text' && (
                <textarea
                  value={pendingText}
                  onChange={(event) => setPendingText(event.target.value)}
                  placeholder={currentQuestion.placeholder}
                  rows={4}
                  required
                />
              )}

              {currentQuestion.type === 'choice' && (
                <div className="options-grid">
                  {currentQuestion.options.map((option) => (
                    <label key={option.value} className={clsx('option-tile', { active: pendingChoice === option.value })}>
                      <input
                        type="radio"
                        name={currentQuestion.id}
                        value={option.value}
                        checked={pendingChoice === option.value}
                        onChange={(event) => setPendingChoice(event.target.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              )}

              {currentQuestion.type === 'multi' && (
                <div className="options-grid">
                  {currentQuestion.options.map((option) => (
                    <label key={option.value} className={clsx('option-tile', { active: pendingMulti.includes(option.value) })}>
                      <input
                        type="checkbox"
                        name={currentQuestion.id}
                        value={option.value}
                        checked={pendingMulti.includes(option.value)}
                        onChange={() => handleMultiToggle(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              )}

              <div className="input-actions">
                <button type="submit" className="btn btn-primary">
                  {currentQuestionIndex + 1 === intakeQuestions.length ? 'See my matches' : 'Next'}
                </button>
              </div>
            </form>
          )}

          {completed && (
            <div className="chat-complete">
              <p>
                Ready for a new check-in? Capture fresh progress anytime -- it helps our
                practitioners fine-tune dosing over time.
              </p>
              <button type="button" className="btn btn-secondary" onClick={resetIntake}>
                Start a fresh conversation
              </button>
            </div>
          )}
        </div>

        <aside className="insights-column">
          <div className="insights-header">
            <h3>{recommendationsHeader}</h3>
            <p>
              Every preview comes with a human review window. Our platform stores the
              reasoning trail so you and your practitioner can see exactly how a formula
              was mapped from your answers.
            </p>
          </div>

          <div className="recommendation-stack">
            {completed ? (
              recommendations.map((match) => (
                <article key={match.formula.id} className="recommendation-card">
                  <header>
                    <span className="tag-pill">{match.formula.pattern}</span>
                    <h4>{match.formula.name}</h4>
                    {match.formula.nickname && <p className="nickname">{match.formula.nickname}</p>}
                  </header>

                  <p className="formula-description">{match.formula.description}</p>

                  <div className="formula-meta">
                    <div>
                      <strong>Why it surfaced</strong>
                      <ul>
                        {match.reasons.map((reason, index) => (
                          <li key={index}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <strong>Hero herbs</strong>
                      <ul>
                        {match.formula.ingredients.map((ingredient) => (
                          <li key={ingredient}>{ingredient}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="formula-footer">
                    <p className="caution">Warning: {match.formula.caution}</p>
                    <p className="form-factor">Preferred form: {match.formula.form}</p>
                  </div>
                </article>
              ))
            ) : (
              <div className="catalogue-preview">
                <h4>Sample formulations in our library</h4>
                <ul>
                  {catalogue.map((formula) => (
                    <li key={formula.id}>
                      <strong>{formula.name}</strong>
                      <span>{formula.pattern}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="safety-box glass-panel">
            <h4>Safety & clinician touchpoints</h4>
            <ul>
              {(completed ? safetyNotes : [
                'Every intake is reviewed by a licensed practitioner before fulfilment.',
                'We track herb-drug interactions and log rationales for each shipment.',
                'Escalate red-flag symptoms for physician referrals immediately.'
              ]).map((note, index) => (
                <li key={index}>{note}</li>
              ))}
            </ul>
            <p className="api-note">
              Secure hand-off happens over our encrypted intake endpoint:{' '}
              <code>{API_BASE}/intake</code>
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default ChatExperience;
